import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { flexRender } from '@tanstack/react-table';
// v8 surface, kept behind `/legacy` in react-table 9 — see components/data-table.tsx.
import { getCoreRowModel, legacyCreateColumnHelper, useLegacyTable, } from '@tanstack/react-table/legacy';
import { arrayMove } from '@dnd-kit/sortable';
import { ChevronDown, ChevronUp, Pencil, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { toggleTrackingLinkStatus } from '@/services/tracking-link';
import { fetchTrackingLinks, reorderTrackingLinks } from '@/services/campaign';
import { errorHandler } from '@/lib/utils';
import { resolveHref } from '@/lib/string';
import { TrackingLinkDialog } from '@/components/dialog/tracking-link-dialog';
import { DeleteTrackingLinkDialog } from '@/routes/console/_authenticated/_layout/campaigns/$publicId/_layout/-features/delete-tracking-link-dialog';
import { TrackingLinkEntryPoints } from '@/components/tracking-link-entry-points';
const columnHelper = legacyCreateColumnHelper();
export const TrackingLinksTable = ({ campaign }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const { data: trackingLinks = [] } = useQuery({
        queryKey: ['campaign-tracking-links', campaign.publicId],
        queryFn: () => fetchTrackingLinks(campaign.publicId),
    });
    const [data, setData] = React.useState(trackingLinks);
    React.useEffect(() => {
        setData(trackingLinks);
    }, [trackingLinks]);
    const getOrder = React.useCallback((links) => links.map((l) => l.publicId).join('|'), []);
    const baselineOrderRef = React.useRef(getOrder(trackingLinks));
    const isDirty = React.useMemo(() => getOrder(data) !== baselineOrderRef.current, [data, getOrder]);
    const handeSuccess = React.useCallback(() => {
        queryClient.invalidateQueries({ queryKey: ['campaign-tracking-links', campaign.publicId] });
    }, [queryClient, campaign.publicId]);
    const { mutate: mutateStatus, isPending: isToggling } = useMutation({
        mutationFn: toggleTrackingLinkStatus,
        onSuccess: (res) => {
            toast.success(res.message);
            handeSuccess();
        },
        onError: errorHandler,
    });
    const { mutate: applyOrder, isPending: isApplying } = useMutation({
        mutationFn: reorderTrackingLinks,
        onSuccess: (res) => {
            toast.success(res.message);
            handeSuccess();
        },
        onError: errorHandler,
    });
    /* ---------------- reorder helpers ---------------- */
    const moveUp = React.useCallback((index) => {
        if (index === 0)
            return;
        setData((prev) => arrayMove(prev, index, index - 1));
    }, []);
    const moveDown = React.useCallback((index) => {
        if (index === data.length - 1)
            return;
        setData((prev) => arrayMove(prev, index, index + 1));
    }, [data.length]);
    const handleApply = React.useCallback(() => {
        applyOrder({
            publicId: campaign.publicId,
            data: { orderedIds: data.map((tl) => tl.id) },
        });
    }, [applyOrder, campaign.publicId, data]);
    const handleReset = React.useCallback(() => {
        setData(trackingLinks);
        baselineOrderRef.current = getOrder(trackingLinks);
    }, [trackingLinks, getOrder]);
    /* ---------------- columns ---------------- */
    const columns = React.useMemo(() => [
        columnHelper.display({
            id: 'order',
            header: t('order', { defaultValue: 'Order' }),
            size: 80,
            cell: ({ row }) => (<div className="flex flex-col items-center gap-1">
                        <Button variant="ghost" size="icon" disabled={row.index === 0} onClick={() => moveUp(row.index)} title={t('move_up', { defaultValue: 'Move up' })}>
                            <ChevronUp className="h-4 w-4"/>
                        </Button>
                        <Button variant="ghost" size="icon" disabled={row.index === data.length - 1} onClick={() => moveDown(row.index)} title={t('move_down', { defaultValue: 'Move down' })}>
                            <ChevronDown className="h-4 w-4"/>
                        </Button>
                    </div>),
        }),
        columnHelper.accessor('label', {
            header: () => t('campaigns.tracking_links.table.label', {
                ns: 'glossary',
            }),
            cell: ({ row: { original } }) => (<>
                        <div className="font-medium">{original.label}</div>
                        <div className="text-xs text-muted-foreground break-all">ID: tl-{original.publicId}</div>
                    </>),
        }),
        columnHelper.accessor('targetUrl', {
            header: () => t('campaigns.tracking_links.table.target_url', {
                ns: 'glossary',
            }),
            cell: ({ row: { original } }) => (<a href={resolveHref(original)} target="_blank" rel="noreferrer" className="text-cyan-400 hover:underline break-all">
                        {original.targetUrl}
                    </a>),
        }),
        columnHelper.display({
            id: 'entry_points',
            header: () => t('campaigns.tracking_links.table.entry_points', {
                ns: 'glossary',
            }),
            cell: ({ row: { original } }) => <TrackingLinkEntryPoints trackingLink={original}/>,
        }),
        columnHelper.accessor('active', {
            header: () => t('campaigns.tracking_links.table.status', {
                ns: 'glossary',
            }),
            cell: ({ row: { original } }) => (<div className="flex items-center gap-2 justify-center">
                        <Switch checked={original.active} disabled={isToggling} onCheckedChange={(checked) => mutateStatus({
                    publicId: original.publicId,
                    active: checked,
                })}/>
                        <span className="text-xs text-muted-foreground">
                            {original.active ? t('active') : t('inactive')}
                        </span>
                    </div>),
        }),
        columnHelper.display({
            id: 'actions',
            header: t('actions', {
                ns: 'glossary',
                defaultValue: 'Actions',
            }),
            size: 120,
            cell: ({ row: { original } }) => (<div className="flex items-center justify-center gap-2">
                        <TrackingLinkDialog campaign={campaign} trackingLink={original} onSucceed={handeSuccess} trigger={<Button variant="ghost" size="icon">
                                    <Pencil className="h-4 w-4"/>
                                </Button>}/>
                        <DeleteTrackingLinkDialog link={original} onDeleted={handeSuccess} trigger={<Button variant="ghost" size="icon" className="text-destructive">
                                    <Trash2 className="h-4 w-4"/>
                                </Button>}/>
                    </div>),
        }),
    ], [t, data.length, moveUp, moveDown, mutateStatus, isToggling, campaign, handeSuccess]);
    const table = useLegacyTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        getRowId: (row) => row.publicId,
    });
    /* ---------------- render ---------------- */
    return (<Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3">
                <div>
                    <CardTitle>{t('analytics.tlp.title', { ns: 'glossary' })}</CardTitle>
                    <CardDescription>
                        {t('analytics.tlp.description', {
            ns: 'glossary',
        })}
                    </CardDescription>
                </div>

                <div className="flex items-center gap-2">
                    {isDirty && (<>
                            <Button variant="ghost" size="sm" onClick={handleReset} disabled={isApplying}>
                                {t('reset')}
                            </Button>
                            <Button size="sm" onClick={handleApply} disabled={isApplying}>
                                {isApplying ? t('saving') : t('apply')}
                            </Button>
                        </>)}

                    <TrackingLinkDialog campaign={campaign} onSucceed={handeSuccess}/>
                </div>
            </CardHeader>

            <CardContent>
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            {table.getHeaderGroups().map((hg) => (<TableRow key={hg.id}>
                                    {hg.headers.map((header) => (<TableHead key={header.id} className="text-center">
                                            {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                                        </TableHead>))}
                                </TableRow>))}
                        </TableHeader>

                        <TableBody>
                            {table.getRowModel().rows.length ? (table.getRowModel().rows.map((row) => (<TableRow key={row.id}>
                                        {row.getVisibleCells().map((cell) => (<TableCell key={cell.id} className="text-center">
                                                {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                            </TableCell>))}
                                    </TableRow>))) : (<TableRow>
                                    <TableCell colSpan={columns.length} className="h-24 text-center">
                                        {t('no_data', { ns: 'table' })}
                                    </TableCell>
                                </TableRow>)}
                        </TableBody>
                    </Table>
                </div>
            </CardContent>
        </Card>);
};
TrackingLinksTable.displayName = 'TrackingLinksTable';
